import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import store from "../../reduxStore/store";
import { formatIssueDateFromObject, formatDueDateFromObject, formatOrderDate, getPaymentTermsDescription as getPaymentTermsDescriptionFromObject } from "../SharedFunctions";
export function PrintInvoiceBL(orderData, salesRepName) {
    var _a, _b, _c, _d, _e, _f, _g;
    var t = useTranslation().t;
    var selectFeatures = (_c = (_b = (_a = store.getState()) === null || _a === void 0 ? void 0 : _a.salesRepData) === null || _b === void 0 ? void 0 : _b.baseData) === null || _c === void 0 ? void 0 : _c.select;
    var showPaymentTermsOption = (_d = selectFeatures === null || selectFeatures === void 0 ? void 0 : selectFeatures.PaymentTerms) !== null && _d !== void 0 ? _d : false;
    // Extract billing and shipping addresses from Shopify order
    var shippingAddress = (orderData === null || orderData === void 0 ? void 0 : orderData.shipping_address) || {};
    var billingAddress = (orderData === null || orderData === void 0 ? void 0 : orderData.billing_address) || {};
    /** Payment terms: label for type (e.g. Due on receipt, Net 45). */
    var getPaymentTermsDescription = function () { return getPaymentTermsDescriptionFromObject(orderData === null || orderData === void 0 ? void 0 : orderData.paymentTerms); };
    /** Issue date: only for Fixed/Net; "-" for Receipt/Fulfillment. */
    var getIssueDate = function () { return formatIssueDateFromObject(orderData === null || orderData === void 0 ? void 0 : orderData.paymentTerms); };
    /** Due date formatted for print (uses same logic as Draft/YourSales/Pending). */
    var getDueDate = function () { return formatDueDateFromObject(orderData === null || orderData === void 0 ? void 0 : orderData.paymentTerms); };
    var getPaidDate = function () { return formatOrderDate(orderData === null || orderData === void 0 ? void 0 : orderData.processed_at); };
    var getOrderDate = function () { return formatOrderDate(orderData === null || orderData === void 0 ? void 0 : orderData.created_at); };
    /** Whether to show payment terms block on invoice (feature on, or order has PT data). */
    var hasPaymentTerms = useMemo(function () {
        var pt = orderData === null || orderData === void 0 ? void 0 : orderData.paymentTerms;
        var orderHasPaymentTerms = pt != null && (pt.paymentTermsType || pt.paymentTermType || pt.dueDate || pt.issueDate || pt.netDays != null);
        return showPaymentTermsOption || orderHasPaymentTerms;
    }, [orderData === null || orderData === void 0 ? void 0 : orderData.paymentTerms, showPaymentTermsOption]);
    // Table header configuration
    var tableHeaders = [
        { id: "items", label: t("yourSales_column1"), align: "left" },
        { id: "pricePerUnit", label: t("Price_Unit"), align: "left" },
        { id: "quantity", label: t("yourSales_column3"), align: "center" },
        { id: "totalPrice", label: t("yourSales_column7"), align: "right" },
    ];
    // Get customer name from Shopify customers object
    var getCustomerName = function () {
        var _a, _b;
        if (((_a = orderData === null || orderData === void 0 ? void 0 : orderData.customers) === null || _a === void 0 ? void 0 : _a.first_name) && ((_b = orderData === null || orderData === void 0 ? void 0 : orderData.customers) === null || _b === void 0 ? void 0 : _b.last_name)) {
            return "".concat(orderData.customers.first_name, " ").concat(orderData.customers.last_name);
        }
        return "-";
    };
    // Format address for display
    var formatAddress = function (address) {
        if (!address || !address.address1)
            return "No address provided";
        var parts = [];
        if (address.address1)
            parts.push(address.address1);
        if (address.address2)
            parts.push(address.address2);
        var cityStateZip = [address.city, address.province, address.zip].filter(Boolean).join(" ");
        if (cityStateZip)
            parts.push(cityStateZip);
        if (address.country)
            parts.push(address.country);
        return parts.join("\n");
    };
    // Get notes from order
    var getNotes = function () {
        return (orderData === null || orderData === void 0 ? void 0 : orderData.note) || "No notes from customer";
    };
    // Get order status and badge color
    var getOrderStatus = function () {
        var _a;
        var financialStatus = (_a = orderData === null || orderData === void 0 ? void 0 : orderData.financial_status) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        if (financialStatus === "paid") {
            return { label: "Paid", color: "#4caf50" }; // Green
        }
        else if (financialStatus === "pending" || financialStatus === "authorized" || financialStatus === "partially_paid") {
            return { label: "Draft", color: "#2196f3" }; // Blue
        }
        else {
            return { label: "Unpaid", color: "#9e9e9e" }; // Grey
        }
    };
    // Memoize order status to avoid recalculating on every render
    var orderStatus = useMemo(function () { return getOrderStatus(); }, [orderData === null || orderData === void 0 ? void 0 : orderData.financial_status]);
    // Get sales rep name - use prop if available, otherwise fallback to Redux store
    var getSalesRepName = function () {
        var _a, _b, _c, _d, _e, _f;
        // Use the prop (already populated by parent from note_attributes or activeClientObj)
        if (salesRepName === null || salesRepName === void 0 ? void 0 : salesRepName.trim()) {
            return salesRepName;
        }
        // Fallback to Redux store
        var loggedInRepData = (_b = (_a = store.getState()) === null || _a === void 0 ? void 0 : _a.salesRepData) === null || _b === void 0 ? void 0 : _b.loggedInRepDetails;
        var singleSalesRep = (_e = (_d = (_c = store.getState()) === null || _c === void 0 ? void 0 : _c.salesRepData) === null || _d === void 0 ? void 0 : _d.baseData) === null || _e === void 0 ? void 0 : _e.SingleSalesRep;
        if ((_f = loggedInRepData === null || loggedInRepData === void 0 ? void 0 : loggedInRepData.customerName) === null || _f === void 0 ? void 0 : _f.trim()) {
            return loggedInRepData.customerName;
        }
        if (singleSalesRep === null || singleSalesRep === void 0 ? void 0 : singleSalesRep.SalesRepName) {
            var fullName = "".concat(singleSalesRep.SalesRepName || "", " ").concat(singleSalesRep.SalesRepLastName || "").trim();
            if (fullName)
                return fullName;
        }
        return "-";
    };
    // Calculate order totals
    var items = (orderData === null || orderData === void 0 ? void 0 : orderData.line_items) || (orderData === null || orderData === void 0 ? void 0 : orderData.lineitems) || [];
    var subtotalAfterLineDiscount = items.reduce(function (sum, item) {
        var price = parseFloat(item.price || "0");
        var qty = Number(item.quantity || 0);
        var lineDiscount = parseFloat(item.total_discount || "0");
        return sum + price * qty - lineDiscount;
    }, 0);
    var totalDiscountFromApi = parseFloat((_f = (_e = orderData === null || orderData === void 0 ? void 0 : orderData.current_total_discounts) !== null && _e !== void 0 ? _e : orderData === null || orderData === void 0 ? void 0 : orderData.total_discounts) !== null && _f !== void 0 ? _f : "0");
    var lineItemDiscountTotal = items.reduce(function (sum, item) { return sum + parseFloat(item.total_discount || "0"); }, 0);
    var subtotalDiscountOnly = totalDiscountFromApi - lineItemDiscountTotal;
    var tax = parseFloat((orderData === null || orderData === void 0 ? void 0 : orderData.total_tax) || "0");
    var shippingCharges = Number((_g = orderData === null || orderData === void 0 ? void 0 : orderData.shippingCharges) !== null && _g !== void 0 ? _g : 0);
    var grandTotal = subtotalAfterLineDiscount - subtotalDiscountOnly + tax + shippingCharges;
    var showDiscountRow = Math.abs(subtotalDiscountOnly) > 0.0001;
    return {
        getCustomerName: getCustomerName,
        formatAddress: formatAddress,
        getNotes: getNotes,
        getOrderStatus: getOrderStatus,
        getSalesRepName: getSalesRepName,
        shippingAddress: shippingAddress,
        billingAddress: billingAddress,
        orderStatus: orderStatus,
        tableHeaders: tableHeaders,
        items: items,
        subtotalAfterLineDiscount: subtotalAfterLineDiscount,
        totalDiscountFromApi: totalDiscountFromApi,
        lineItemDiscountTotal: lineItemDiscountTotal,
        subtotalDiscountOnly: subtotalDiscountOnly,
        tax: tax,
        shippingCharges: shippingCharges,
        grandTotal: grandTotal,
        showDiscountRow: showDiscountRow,
        getPaymentTermsDescription: getPaymentTermsDescription,
        getIssueDate: getIssueDate,
        getDueDate: getDueDate,
        getPaidDate: getPaidDate,
        getOrderDate: getOrderDate,
        hasPaymentTerms: hasPaymentTerms,
    };
}
